import { useState, type FormEvent } from "react";
import { supabase } from "../../lib/supabase";
import { Wordmark } from "../../components/AppShell";
import { Button, Card, Spinner } from "../../components/ui";

type Mode = "signin" | "signup" | "link";

type Status =
  | { kind: "idle" }
  | { kind: "sending" }
  | { kind: "sent"; email: string; why: "link" | "confirm" }
  | { kind: "error"; message: string };

const MIN_PASSWORD = 8;

/**
 * Sign in with email and password. Membership is still enforced by the
 * allowlist (the before_user_created hook and the auth.users trigger), so
 * "create account" only works for addresses the group has added.
 *
 * The email link stays as a fallback: accounts made before passwords existed
 * use it once, then set a password in Settings.
 */
export function LoginPage() {
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  function switchTo(next: Mode) {
    setMode(next);
    setStatus({ kind: "idle" });
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const address = email.trim().toLowerCase();
    if (!address) return;

    setStatus({ kind: "sending" });

    if (mode === "link") {
      const { error } = await supabase.auth.signInWithOtp({
        email: address,
        options: { emailRedirectTo: window.location.origin },
      });
      if (error) return fail(error.message);
      setStatus({ kind: "sent", email: address, why: "link" });
      return;
    }

    if (mode === "signup") {
      const { data, error } = await supabase.auth.signUp({ email: address, password });
      if (error) return fail(error.message);
      // With email confirmation switched on there is no session yet.
      if (!data.session) setStatus({ kind: "sent", email: address, why: "confirm" });
      return;
    }

    const { error } = await supabase.auth.signInWithPassword({ email: address, password });
    if (error) return fail(error.message);
    // On success the auth listener swaps this screen out.
  }

  function fail(raw: string) {
    let message = raw;
    if (/invite|allowlist|not allowed|403/i.test(raw)) {
      // The before_user_created hook rejects addresses that are not on the list.
      message = "That email isn't on the list. Ask the group to add you.";
    } else if (/invalid login credentials/i.test(raw)) {
      message = "Wrong email or password. First time here? Create an account below.";
    } else if (/already registered|already been registered/i.test(raw)) {
      message =
        "That email already has an account. Sign in, or use the email link to get in and set a password.";
    } else if (/password/i.test(raw) && /(short|least|characters)/i.test(raw)) {
      message = `Use at least ${MIN_PASSWORD} characters for your password.`;
    }
    setStatus({ kind: "error", message });
  }

  const sending = status.kind === "sending";

  return (
    <main className="flex min-h-dvh flex-col justify-center px-7 pb-safe pt-safe">
      <div className="mx-auto flex w-full max-w-sm flex-col gap-7">
        <div className="text-center">
          <Wordmark size="lg" />
        </div>

        {status.kind === "sent" ? (
          <Card className="items-center gap-2 p-6 text-center">
            <h2 className="text-xl">Check your email</h2>
            <p className="text-[13px] text-neutral-500">
              {status.why === "link" ? "A sign-in link is on its way to " : "Confirm your address at "}
              <span className="font-semibold text-text">{status.email}</span>. Open it on this
              device.
            </p>
            <Button variant="ghost" onClick={() => switchTo("signin")} className="mt-1">
              back
            </Button>
          </Card>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-2.5">
            <label className="flex flex-col gap-1.5">
              <span className="text-xs text-neutral-500">email</span>
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@domain.com"
                className="input"
              />
            </label>

            {mode !== "link" && (
              <label className="flex flex-col gap-1.5">
                <span className="text-xs text-neutral-500">
                  password{mode === "signup" && ` (${MIN_PASSWORD}+ characters)`}
                </span>
                <input
                  type="password"
                  autoComplete={mode === "signup" ? "new-password" : "current-password"}
                  required
                  minLength={mode === "signup" ? MIN_PASSWORD : undefined}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="input"
                />
              </label>
            )}

            <Button type="submit" disabled={sending} className="btn-block mt-2">
              {sending ? (
                <Spinner className="border-t-current" />
              ) : mode === "signin" ? (
                "Sign in"
              ) : mode === "signup" ? (
                "Create account"
              ) : (
                "Send link"
              )}
            </Button>

            {status.kind === "error" && (
              <p role="alert" className="pt-1 text-center text-[13px] text-[#b4332a]">
                {status.message}
              </p>
            )}

            <div className="flex flex-col items-center gap-1 pt-2">
              {mode === "signin" ? (
                <Button variant="ghost" type="button" onClick={() => switchTo("signup")}>
                  New here? Create an account
                </Button>
              ) : (
                <Button variant="ghost" type="button" onClick={() => switchTo("signin")}>
                  Have an account? Sign in
                </Button>
              )}
              {mode !== "link" && (
                <Button variant="ghost" type="button" onClick={() => switchTo("link")}>
                  Email me a sign-in link instead
                </Button>
              )}
            </div>
          </form>
        )}

        <p className="text-center text-xs text-neutral-500">invite only</p>
      </div>
    </main>
  );
}
